import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Phone, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CallCenterKPIsWidget = () => {
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');

  useEffect(() => {
    fetchKPIs();
  }, [period]);

  const fetchKPIs = async () => {
    try {
      const response = await axios.get(`${API}/director/call-center-kpis`, {
        params: { period }
      });
      setKpiData(response.data);
    } catch (error) {
      console.error('Error fetching call center KPIs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderTrend = (value) => {
    if (!value) return null;
    const isPositive = value > 0;
    return (
      <div className={`flex items-center text-xs ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
        {Math.abs(value).toFixed(1)}%
      </div>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-3">
      {/* Filter */}
      <div className="flex justify-between items-center">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[130px] h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="outline" className="text-xs">
          <Phone className="h-3 w-3 mr-1" />
          CallRail Integration
        </Badge>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 gap-2">
        <div className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition">
          <div className="text-xs text-gray-600 mb-1">Total Calls</div>
          <div className="flex justify-between items-end">
            <div className="text-2xl font-bold">{kpiData?.total_calls || 0}</div>
            {renderTrend(kpiData?.trend_total_calls)}
          </div>
        </div>

        <div className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition">
          <div className="text-xs text-gray-600 mb-1">Appointments</div>
          <div className="flex justify-between items-end">
            <div className="text-2xl font-bold">{kpiData?.demo_appointments_set || 0}</div>
            {renderTrend(kpiData?.trend_appointments_set)}
          </div>
        </div>

        <div className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition">
          <div className="text-xs text-gray-600 mb-1">Avg Call Length</div>
          <div className="text-2xl font-bold">{formatTime(kpiData?.avg_call_length || 0)}</div>
        </div>

        <div className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition">
          <div className="text-xs text-gray-600 mb-1">Call Quality</div>
          <div className="text-2xl font-bold">{kpiData?.call_quality_score || 0}%</div>
        </div>
      </div>

      {/* Call Breakdown */}
      <div className="p-3 border rounded-lg bg-purple-50 dark:bg-purple-950">
        <div className="text-xs text-gray-600 mb-2">Call Distribution</div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <div className="font-semibold">{kpiData?.inbound_calls || 0}</div>
            <div className="text-xs text-gray-600">Inbound</div>
          </div>
          <div>
            <div className="font-semibold">{kpiData?.outbound_calls || 0}</div>
            <div className="text-xs text-gray-600">Outbound</div>
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-500 text-center">📞 CallRail API integration ready</p>
    </div>
  );
};

export default CallCenterKPIsWidget;